import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, eur, errMsg } from "@/lib/api";

const EMPTY = { asin: "", title: "", description: "", category: "", image: "", amazon_price: "", amazon_list_price: "", amazon_url: "", active: true };

const parseAsin = (v) => {
  const m = v.match(/\/(?:dp|gp\/product)\/([A-Z0-9]{10})/i);
  return m ? m[1].toUpperCase() : v.trim().toUpperCase();
};

const AutoFill = ({ onFill }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const run = async () => {
    setBusy(true);
    setNote("");
    try {
      const { data } = await api.get("/admin/amazon/lookup", { params: { url } });
      const { configured, exists, ...fields } = data;
      onFill(fields);
      if (exists) toast.warning("Questo prodotto è già nel catalogo");
      setNote(configured ? "Dati compilati da Amazon: controlla e scegli la categoria." :
        "Ho compilato ASIN e link. Il resto si compilerà da solo quando collegherai le credenziali Amazon Creators API.");
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#0033FF] p-3 space-y-2" data-testid="autofill-box">
      <span className="tag text-[#0033FF]">Incolla link Amazon</span>
      <div className="flex gap-2">
        <input data-testid="autofill-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.amazon.it/.../dp/B0XXXXXXXX" className="field text-sm" />
        <button type="button" data-testid="autofill-button" disabled={busy || !url.trim()} onClick={run} className="btn-primary !bg-[#0033FF] hover:!bg-[#0022AA] px-4 text-sm whitespace-nowrap">
          {busy ? "Cerco..." : "Compila automaticamente"}
        </button>
      </div>
      {note && <p className="text-xs text-[#555]" data-testid="autofill-note">{note}</p>}
    </div>
  );
};

const ProductForm = ({ initial, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const fill = (d) => setF((cur) => ({ ...cur, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v !== null && v !== "")) }));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const price = parseFloat(f.amazon_price) || 0;

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, asin: parseAsin(f.asin), amazon_price: +f.amazon_price, amazon_list_price: +f.amazon_list_price, amazon_url: f.amazon_url || null };
    try {
      if (f.id) await api.put(`/admin/products/${f.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Prodotto salvato");
      onSaved();
    } catch (er) { toast.error(errMsg(er)); }
  };

  const input = (k, label, extra = {}) => (
    <label className="block">
      <span className="tag text-[#555] block mb-1">{label}</span>
      <input data-testid={`product-form-${k}`} required={k !== "amazon_url"} value={f[k]} onChange={set(k)} className="field" {...extra} />
    </label>
  );

  return (
    <form onSubmit={save} className="space-y-4">
      {!f.id && <AutoFill onFill={fill} />}
      {input("asin", "ASIN o link Amazon", { placeholder: "B0XXXXXXXX o https://www.amazon.it/dp/..." })}
      {input("title", "Titolo")}
      <label className="block">
        <span className="tag text-[#555] block mb-1">Descrizione</span>
        <textarea data-testid="product-form-description" required rows={3} value={f.description} onChange={set("description")} className="field" />
      </label>
      <div className="grid grid-cols-2 gap-3">
        {input("category", "Categoria")}
        {input("image", "URL immagine")}
        {input("amazon_price", "Prezzo Amazon scontato €", { type: "number", step: "0.01", min: "0.01" })}
        {input("amazon_list_price", "Prezzo Amazon pieno €", { type: "number", step: "0.01", min: "0.01" })}
      </div>
      {input("amazon_url", "Link Amazon (opzionale)")}
      <label className="flex items-center gap-2 text-sm">
        <input data-testid="product-form-active" type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Visibile nel negozio
      </label>
      <div className="border border-[#0033FF] p-3 font-mono text-sm" data-testid="product-form-preview">
        {eur(price)} + 20% = <b className="text-[#0033FF]">{eur(price * 1.2)}</b> prezzo cliente
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="btn-ghost px-4 h-10">Annulla</button>
        <button data-testid="product-form-submit" className="btn-primary px-6 h-10">Salva</button>
      </div>
    </form>
  );
};

export default function AdminProducts() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/products").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Prodotto eliminato");
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-end justify-between">
        <div>
          <span className="tag text-[#0033FF]">Catalogo</span>
          <h1 className="font-display font-black text-4xl tracking-tight">Prodotti</h1>
        </div>
        <button data-testid="add-product-button" onClick={() => setEdit(EMPTY)} className="btn-primary h-10 px-4 flex items-center gap-2 text-sm">
          <Plus size={16} /> Importa da Amazon
        </button>
      </div>
      <div className="border border-[#0F0F0F] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F] text-white">
            <tr>{["", "ASIN", "Prodotto", "Amazon", "Vendita", "Margine", "Stato", ""].map((h, i) => <th key={i} className="tag text-left px-3 py-2 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t border-[#0F0F0F]/15 hover:bg-[#FBFBFB]" data-testid={`admin-product-row-${p.id}`}>
                <td className="px-3 py-2"><img src={p.image} alt="" className="w-10 h-10 object-cover border border-[#0F0F0F]" /></td>
                <td className="px-3 py-2 font-mono text-xs">
                  <a href={p.amazon_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#0033FF]">{p.asin} <ExternalLink size={11} /></a>
                </td>
                <td className="px-3 py-2 max-w-xs truncate">{p.title}</td>
                <td className="px-3 py-2 font-mono">{eur(p.amazon_price)}</td>
                <td className="px-3 py-2 font-mono font-semibold">{eur(p.price)}</td>
                <td className="px-3 py-2 font-mono text-[#0033FF]">{eur(p.price - p.amazon_price)}</td>
                <td className="px-3 py-2"><span className={`tag ${p.active ? "text-[#00A651]" : "text-[#555]"}`}>{p.active ? "Attivo" : "Nascosto"}</span></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button data-testid={`edit-product-${p.id}`} onClick={() => setEdit(p)} className="p-1.5 hover:text-[#0033FF]"><Pencil size={15} /></button>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => del(p.id)} className="p-1.5 hover:text-[#FF3333]"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-none border border-[#0F0F0F] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogTitle className="font-display font-black text-2xl">{edit?.id ? "Modifica prodotto" : "Importa prodotto Amazon"}</DialogTitle>
          <DialogDescription className="sr-only">Dati prodotto Amazon</DialogDescription>
          {edit && <ProductForm initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
